import { ROTransaction, RWTransaction, runWithROTransaction } from '../../../db/db'
import {
  BaseDBService,
  BaseDBServiceOpts,
  Context,
  CoreDict,
  enumGetKeys,
  enumGetValues,
  throwObjectNotFound,
  trCore,
  validateOptionalNumber,
} from '../../../common'
import { CreateEventMode, ModelEvent, ModelEventCreator, ModelEventsProcessor } from '../../events'
import { ProductCategory } from '../../productCategories'
import {
  CONSUMPTION_RATES_ID,
  ConsumptionRate,
  ConsumptionRates,
  ConsumptionRatesRepository,
  ConsumptionRatesService,
  DBStoredConsumptionRate,
  DEFAULT_AVERAGE_RATE_BY_CATEGORY,
  MAX_COUPONS_BY_CATEGORY,
} from './consumptionRates'
import { Permissions } from '../../authorization'
import { forIn } from 'lodash'

enum EventType {
  CONSUMPTION_RATE_UPDATE = 'CONSUMPTION_RATE_UPDATE',
  CONSUMPTION_RATE_MERGE = 'CONSUMPTION_RATE_MERGE',
}

type UpdateConsumptionRatePayload = {
  productCategory: ProductCategory
  averageRate?: number
  couponsCountPerMonth?: number
}

type MergeConsumptionRatePayload = DBStoredConsumptionRate[]

interface DBConsumptionRatesServiceOpts extends BaseDBServiceOpts<CoreDict> {
  consumptionRatesRepository: ConsumptionRatesRepository
}

export class DBConsumptionRatesService
  extends BaseDBService<DBConsumptionRatesServiceOpts, CoreDict>
  implements ConsumptionRatesService
{
  private validateCouponsCountPerMonth = (productCategory: ProductCategory, couponsCountPerMonth?: number) => {
    return validateOptionalNumber('<7599264c> Coupons count per month', couponsCountPerMonth, {
      minValue: 0,
      maxValue: MAX_COUPONS_BY_CATEGORY[productCategory],
    })
  }

  private validateAverageRate = (couponsCountPerMonth?: number) => {
    return validateOptionalNumber('<e2b9952e> Average rate', couponsCountPerMonth, {
      minValue: 0,
    })
  }

  constructor(opts: DBConsumptionRatesServiceOpts) {
    super('DBConsumptionRatesService', opts)
    this.logger.debug({}, '<cd9bde9d> Initialized')
  }

  async get(ctx: Context): Promise<ConsumptionRates> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.CONSUMPTION_RATES_VIEW)

    const result = await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.consumptionRatesRepository.findAll(tx),
    )

    const consumptionRates: Partial<ConsumptionRates> = {}

    forIn(ProductCategory, category => {
      consumptionRates[category] = {
        averageRate:
          result.find(r => r.productCategory == category)?.averageRate ?? DEFAULT_AVERAGE_RATE_BY_CATEGORY[category],
        couponsCountPerMonth:
          result.find(r => r.productCategory == category)?.couponsCountPerMonth ?? MAX_COUPONS_BY_CATEGORY[category],
      }
    })

    return consumptionRates as ConsumptionRates
  }

  async updateByProductCategory(
    ctx: Context,
    productCategory: ProductCategory,
    opts: Partial<ConsumptionRate>,
  ): Promise<void> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.CONSUMPTION_RATES_MANAGE)

    if (!enumGetKeys(ProductCategory).includes(productCategory)) {
      throwObjectNotFound(
        trCore(d => d.consumptionRates.productCategoryDoesNotExist, productCategory),
        { uid: '<97f8a454>' },
      )
    }

    const event = await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.eventCreator.create(ctx, tx, {
        entityId: CONSUMPTION_RATES_ID,
        type: EventType.CONSUMPTION_RATE_UPDATE,
        mode: CreateEventMode.NEW_OR_UPDATE,
        payload: <UpdateConsumptionRatePayload>{
          productCategory: productCategory,
          averageRate: this.validateAverageRate(opts.averageRate),
          couponsCountPerMonth: this.validateCouponsCountPerMonth(productCategory, opts.couponsCountPerMonth),
        },
      }),
    )

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }
}

export class ConsumptionRatesModelEventProcessor implements ModelEventsProcessor {
  supportedEventTypes: string[] = enumGetValues(EventType)

  constructor(
    private opts: { consumptionRatesRepository: ConsumptionRatesRepository; eventCreator: ModelEventCreator },
  ) {}

  // UPDATED
  async createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    const rates = await this.opts.consumptionRatesRepository.findAll(tx)
    return await this.opts.eventCreator.create(ctx, tx, {
      entityId: entityId,
      mode: CreateEventMode.UPDATE,
      type: EventType.CONSUMPTION_RATE_MERGE,
      payload: rates.map(r => ({ ...r })),
    })
  }

  async process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type as EventType) {
      case EventType.CONSUMPTION_RATE_UPDATE: {
        const p = event.payload as UpdateConsumptionRatePayload
        const cr = (await this.opts.consumptionRatesRepository.findByProductCategory(tx, p.productCategory)) ?? {
          productCategory: p.productCategory,
          averageRate: DEFAULT_AVERAGE_RATE_BY_CATEGORY[p.productCategory],
          couponsCountPerMonth: MAX_COUPONS_BY_CATEGORY[p.productCategory],
        }
        await this.opts.consumptionRatesRepository.save(tx, {
          productCategory: p.productCategory,
          averageRate: p.averageRate ?? cr.averageRate,
          couponsCountPerMonth: p.couponsCountPerMonth ?? cr.couponsCountPerMonth,
        })
        return true
      }
      case EventType.CONSUMPTION_RATE_MERGE: {
        const rates = event.payload as unknown as MergeConsumptionRatePayload
        for (const r of rates) {
          await this.opts.consumptionRatesRepository.save(tx, r)
        }
        return true
      }
    }
    return false
  }
}
